/**
 * Kitchen staff management hooks for HMH Lunch Ordering System.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiConfig } from "../config/msalConfig";

export interface KitchenStaff {
  id: number;
  username: string;
  display_name: string;
  is_active: boolean;
  created_at: string;
  last_login: string | null;
}

type KitchenStaffListResponse = KitchenStaff[];

interface CreateKitchenStaffRequest {
  username: string;
  display_name: string;
}

interface CreateKitchenStaffResponse {
  staff: KitchenStaff;
  pin: string;
}

interface UpdateKitchenStaffRequest {
  display_name?: string;
  is_active?: boolean;
}

interface ResetPinResponse {
  pin: string;
}

/**
 * Hook to fetch kitchen staff list
 */
export function useKitchenStaffList() {
  return useQuery<KitchenStaffListResponse>({
    queryKey: ["kitchen-staff"],
    queryFn: async () => {
      const token = sessionStorage.getItem("hmh_access_token");
      const response = await fetch(`${apiConfig.baseUrl}/admin/kitchen-staff`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch kitchen staff");
      }

      return response.json();
    },
  });
}

/**
 * Hook to create a new kitchen staff member
 */
export function useCreateKitchenStaff() {
  const queryClient = useQueryClient();

  return useMutation<CreateKitchenStaffResponse, Error, CreateKitchenStaffRequest>({
    mutationFn: async (data) => {
      const token = sessionStorage.getItem("hmh_access_token");
      const response = await fetch(`${apiConfig.baseUrl}/admin/kitchen-staff`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to create kitchen staff");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kitchen-staff"] });
    },
  });
}

/**
 * Hook to update a kitchen staff member
 */
export function useUpdateKitchenStaff() {
  const queryClient = useQueryClient();

  return useMutation<KitchenStaff, Error, { id: number; data: UpdateKitchenStaffRequest }>({
    mutationFn: async ({ id, data }) => {
      const token = sessionStorage.getItem("hmh_access_token");
      const response = await fetch(`${apiConfig.baseUrl}/admin/kitchen-staff/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to update kitchen staff");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kitchen-staff"] });
    },
  });
}

/**
 * Hook to delete a kitchen staff member
 */
export function useDeleteKitchenStaff() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      const token = sessionStorage.getItem("hmh_access_token");
      const response = await fetch(`${apiConfig.baseUrl}/admin/kitchen-staff/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to delete kitchen staff");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kitchen-staff"] });
    },
  });
}

/**
 * Hook to reset a kitchen staff member's PIN
 */
export function useResetPin() {
  return useMutation<ResetPinResponse, Error, number>({
    mutationFn: async (id) => {
      const token = sessionStorage.getItem("hmh_access_token");
      const response = await fetch(
        `${apiConfig.baseUrl}/admin/kitchen-staff/${id}/reset-pin`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to reset PIN");
      }

      return response.json();
    },
  });
}
